import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Check,
  Users,
  MapPin,
  Download,
  FileText,
  ChevronDown,
  Layers,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import { CANONICAL_SUBSEGMENTS, MARKET_NAMES, type Condition, type MarketCode } from '@hc/shared';
import { PanelFrame } from '../panel-frame.js';
import { buttonStyle } from '../button.js';
import type { PanelParams } from '../components.js';
import { useCatchmentKey } from '../catchment-link.js';
import { useRollupLink } from '../rollup-link.js';
import { useBuilderState, type ReplacedTarget } from '../builder-state.js';
import { useDockviewApi } from '../dockview-context.js';
import { useAppStore, usePanel, useGlobalCurrency, type LinkGroupId } from '../store.js';
import {
  convertCurrency,
  convertFromEur,
  CURRENCY_SYMBOL,
  GLOBAL_CURRENCIES,
  type GlobalCurrency,
} from '../../currency.js';
import { useScreenerFacets } from '../../api/screener-hooks.js';
import { fetchScreener } from '../../api/screener-client.js';
import {
  usePlatformBuilder,
  useCreateRollupFoundation,
  useCreateWatchlist,
  useAddEntitiesToWatchlist,
  useBuyerMandates,
  useBuyerPortfolios,
} from '../../api/hooks.js';
import type {
  BuilderObjective,
  BuilderAnchorMode,
  PlatformBuilderQuery,
  PlatformBuilderResult,
} from '../../api/client.js';
import {
  exportPortfolioCsv,
  openPortfolioInvestmentMemo,
  type BuildExportMeta,
} from '../platform-builder-export.js';
import { SCREENER_COLUMN_DEFS, type ScreenerRightColumnId } from '../screener-columns.js';
import { useOpenChannelPanel } from '../use-open-channel-panel.js';
import { useSelectLink } from '../select-link.js';
import { formatMoney, formatCount, EMPTY_VALUE } from '../../format.js';

// Platform Builder — top-down portfolio constructor. The user sets a revenue
// target + market + objectives; the panel constructs a candidate buy-list
// (greedy-fill to target, signal-ranked, geographically clustered) and BROADCASTS
// the entity set over its channel. A Watchlist (Ward) panel on the same channel
// renders the set as a browsable table; a Map panel on the same channel pins the
// sites. "Commit to Roll-Up Foundation" persists the portfolio for per-entity
// curation + pro-forma modelling. GB v1 (signals are GB-only).
// See handover/panels/Platform_Builder_BUILD_SPEC.md.

const OBJECTIVES: Array<{ key: BuilderObjective; label: string; hint: string }> = [
  {
    key: 'gettability',
    label: 'Gettability',
    hint: 'Favour owners likely to sell (Seller Readiness)',
  },
  { key: 'fewestDeals', label: 'Fewest deals', hint: 'Favour larger targets → fewer acquisitions' },
  { key: 'tightestCluster', label: 'Tightest cluster', hint: 'Favour geographic concentration' },
  {
    key: 'diversifyRegion',
    label: 'Spread regions',
    hint: 'Spread the build across regions (opposite of tightest cluster)',
  },
];

// Concentrate (tightestCluster) and spread (diversifyRegion) are opposites —
// selecting one clears the other.
const REGION_OBJECTIVES: BuilderObjective[] = ['tightestCluster', 'diversifyRegion'];

// Operator-size band tiers, defined in EUR (revenue per site) so a band means the
// same ECONOMIC size in every currency. Labels/bounds are derived per display
// currency (converted + rounded), so e.g. the €1m tier shows as ~kr12m in NOK —
// not a literal "1m NOK" (which would be tiny). Keys are currency-independent so
// the selection survives a currency switch.
const SIZE_TIERS_EUR = [0.5e6, 1e6, 3e6, 10e6];

/** Round a millions value to a readable step for band labels. */
function niceM(x: number): number {
  if (x >= 100) return Math.round(x / 25) * 25;
  if (x >= 20) return Math.round(x / 5) * 5;
  if (x >= 5) return Math.round(x);
  if (x >= 1) return Math.round(x * 2) / 2;
  return Math.round(x * 10) / 10;
}

function money(eur: number | null | undefined, ccy: GlobalCurrency): string {
  if (eur == null) return EMPTY_VALUE;
  return formatMoney(convertFromEur(eur, ccy), { currency: CURRENCY_SYMBOL[ccy] });
}

// Tooltip for a revenue-confidence cell — P100 is a filed/reported figure; a
// lower P-band is a modelled estimate (the ±50% empirical-CV standard).
function confidenceTitle(p: string | null, basis: string | null): string {
  const b = basis ? ` · ${basis}` : '';
  if (p === 'P100') return `Filed / reported revenue${b}`;
  if (!p) return 'Revenue basis unknown';
  return `Modelled estimate — ${p} confidence band (reliable within ±50%)${b}`;
}

// Shared control width — every Market field + the Operator-size field use FIELD_W
// (sized for the longest label, e.g. "Physiotherapy & allied health"). The amount
// + currency pair share that same total: HALF_W + PAIR_GAP + HALF_W = FIELD_W.
const FIELD_W = 224;
const PAIR_GAP = 6;
const HALF_W = (FIELD_W - PAIR_GAP) / 2;
// Inline "open a panel" link-button (Buyer context → Mandate / Portfolio panels).
const LINK_BTN: CSSProperties = {
  border: 'none',
  background: 'transparent',
  padding: 0,
  font: 'inherit',
  fontSize: 'var(--t-meta)',
  color: 'var(--accent)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
// Uniform width for the Objectives + Structure toggle buttons (fits the longest
// label, "Tightest cluster", with its tick).
const CHIP_W = 142;

export function PlatformBuilderPanel({ tabId, panelId }: PanelParams) {
  const { t } = useTranslation();
  const ccy = useGlobalCurrency();
  const facets = useScreenerFacets();
  const regionsByCountry = facets.data?.regionsByCountry ?? {};
  // GB v1: the Builder depends on Origination/Seller-Readiness signals AND the
  // consolidation benchmark, both of which are GB-only today. Offering other
  // markets would return an empty pool + a GB-specific confidence note. Expand
  // this list as each market's signals + benchmark land.
  const countryOptions = ['GB'];

  // Seed from the per-panel store so inputs SURVIVE a tab switch (the panel
  // unmounts when its workspace tab is inactive). Captured once at mount.
  const saved0 = useRef(useBuilderState.getState().byPanel[panelId]).current;
  const saveInputs = useBuilderState((s) => s.set);

  // Nothing pre-populated on start — except the currency, which presets to the
  // global display currency.
  const [targetInput, setTargetInput] = useState(saved0?.targetInput ?? '');
  const [revCcy, setRevCcy] = useState<GlobalCurrency | ''>(saved0?.revCcy ?? ccy);
  const [country, setCountry] = useState(saved0?.country ?? '');
  const [subsegment, setSubsegment] = useState(saved0?.subsegment ?? '');
  const [region, setRegion] = useState(saved0?.region ?? '');
  const [objectives, setObjectives] = useState<BuilderObjective[]>(
    saved0?.objectives ?? ['gettability', 'tightestCluster'],
  );
  const [anchorMode, setAnchorMode] = useState<BuilderAnchorMode>(
    saved0?.anchorMode ?? 'aggregation',
  );
  // Minimum anchor size — the user's choice when anchor-led: a % of the target
  // or an absolute amount (in the chosen currency, millions). Default 25%.
  const [anchorSize, setAnchorSize] = useState(saved0?.anchorSize ?? '25');
  const [anchorUnit, setAnchorUnit] = useState<'pct' | 'abs'>(saved0?.anchorUnit ?? 'pct');
  // Optional operator-size band (revenue per site); 'any' = no filter.
  const [sizeBand, setSizeBand] = useState(saved0?.sizeBand ?? 'any');
  // Constraint layer — optional gate on the eligible pool (market-agnostic): drop
  // the bottom regulatory-quality band.
  const [excludeWeakQuality, setExcludeWeakQuality] = useState(saved0?.excludeWeakQuality ?? false);
  // Aggregation sizing intent — pin the cluster's scale by target count or typical
  // size ('auto' = derive from the pool median). aggSizeValue is entered in the
  // build currency, millions (for 'size').
  const [aggSizeMode, setAggSizeMode] = useState<'auto' | 'count' | 'size'>(
    saved0?.aggSizeMode ?? 'auto',
  );
  const [aggSizeValue, setAggSizeValue] = useState(saved0?.aggSizeValue ?? '');
  const [platformEntityId, setPlatformEntityId] = useState<string | null>(
    saved0?.platformEntityId ?? null,
  );
  // Targets the user has asked to replace — excluded from the build (the API
  // re-fills the gap with the next-best alternative(s)). Kept WITH names so each
  // can be restored individually, not just reset all.
  const [excluded, setExcluded] = useState<ReplacedTarget[]>(saved0?.excluded ?? []);
  const excludedIds = useMemo(() => excluded.map((e) => e.id), [excluded]);

  // ── Buyer context — attach a MANDATE (prefills + steers conditions) and ZERO
  // OR ONE named PORTFOLIO (spec §16): only the SELECTED portfolio's holdings
  // are excluded from the buy-list and seed the footprint gap/overlap logic —
  // an advisor's other client portfolios stay dormant. Both are sent to the API
  // (tenant-scoped there); conditions come back buyer-aware when either is
  // attached. Session-only (not persisted).
  const mandates = useBuyerMandates().data?.data ?? [];
  const portfolios = useBuyerPortfolios().data?.data ?? [];
  const [attachedMandateId, setAttachedMandateId] = useState<string | null>(null);
  const [attachedPortfolioId, setAttachedPortfolioId] = useState<string | null>(null);
  const attachedPortfolio = portfolios.find((p) => p.id === attachedPortfolioId) ?? null;

  const allExcludeIds = excludedIds;

  // Attaching a mandate prefills the build inputs it constrains: country (only
  // when singular AND buildable — the builder is GB-only today) and sub-segment.
  useEffect(() => {
    if (!attachedMandateId) return;
    const m = mandates.find((x) => x.id === attachedMandateId);
    if (!m) return;
    if (m.countries.length === 1 && countryOptions.includes(m.countries[0]!)) {
      setCountry(m.countries[0]!);
    }
    if (m.subsegments.length === 1) setSubsegment(m.subsegments[0]!);
    // Intentionally keyed on the attached id only (prefill once on attach).
  }, [attachedMandateId]);

  // Persist inputs across unmount/remount (tab switches).
  useEffect(() => {
    saveInputs(panelId, {
      targetInput,
      revCcy,
      country,
      subsegment,
      region,
      objectives,
      anchorMode,
      anchorSize,
      anchorUnit,
      sizeBand,
      excludeWeakQuality,
      aggSizeMode,
      aggSizeValue,
      platformEntityId,
      excluded,
    });
  }, [
    panelId,
    saveInputs,
    targetInput,
    revCcy,
    country,
    subsegment,
    region,
    objectives,
    anchorMode,
    anchorSize,
    anchorUnit,
    sizeBand,
    excludeWeakQuality,
    aggSizeMode,
    aggSizeValue,
    platformEntityId,
    excluded,
  ]);

  // Drop ingest-noise region values ('Unknown'/blank — entities whose region
  // wasn't resolved) from the picker.
  const regionOptions = (regionsByCountry[country] ?? []).filter(
    (r) => r && r.toLowerCase() !== 'unknown',
  );

  // Money in this panel reads out in the currency the user is BUILDING in (the
  // target's currency), not the global display currency.
  const dispCcy = (revCcy || ccy) as GlobalCurrency;

  // Target revenue in EUR (the API's basis). Entered in the chosen currency,
  // always in millions. Zero until a currency is chosen.
  const targetEur = useMemo(() => {
    const n = Number(targetInput);
    if (!revCcy || !Number.isFinite(n) || n <= 0) return 0;
    return Math.round(convertCurrency(n * 1e6, revCcy, 'EUR'));
  }, [targetInput, revCcy]);

  // The anchor floor in EUR (only when anchor-led).
  const anchorMinRevEur = useMemo(() => {
    if (anchorMode !== 'anchor-led' || !revCcy) return undefined;
    const n = Number(anchorSize);
    if (!Number.isFinite(n) || n <= 0) return undefined;
    return anchorUnit === 'pct'
      ? Math.round((n / 100) * targetEur)
      : Math.round(convertCurrency(n * 1e6, revCcy, 'EUR'));
  }, [anchorMode, anchorSize, anchorUnit, targetEur, revCcy]);

  // Operator-size bands for the display currency, derived from the EUR tiers
  // (so each band is the same real size whatever the currency). Bounds are in
  // EUR (rounded from the per-currency label values, so label and filter match).
  const sizeBands = useMemo(() => {
    const sym = CURRENCY_SYMBOL[dispCcy];
    const [m0 = 0, m1 = 0, m2 = 0, m3 = 0] = SIZE_TIERS_EUR.map((t) =>
      niceM(convertFromEur(t, dispCcy) / 1e6),
    );
    const eur = (m: number) => Math.round(convertCurrency(m * 1e6, dispCcy, 'EUR'));
    const fmt = (m: number) => `${sym}${m}m`;
    return [
      {
        key: 'any',
        label: 'any size',
        minEur: null as number | null,
        maxEur: null as number | null,
      },
      { key: 'b0', label: `< ${fmt(m0)} / site`, minEur: null as number | null, maxEur: eur(m0) },
      { key: 'b1', label: `${fmt(m0)}–${fmt(m1)} / site`, minEur: eur(m0), maxEur: eur(m1) },
      { key: 'b2', label: `${fmt(m1)}–${fmt(m2)} / site`, minEur: eur(m1), maxEur: eur(m2) },
      { key: 'b3', label: `${fmt(m2)}–${fmt(m3)} / site`, minEur: eur(m2), maxEur: eur(m3) },
      { key: 'b4', label: `${fmt(m3)}+ / site`, minEur: eur(m3), maxEur: null as number | null },
    ];
  }, [dispCcy]);

  const sizeFilter = useMemo<{ minRevPerSiteEur?: number; maxRevPerSiteEur?: number }>(() => {
    const band = sizeBands.find((b) => b.key === sizeBand);
    if (!band || (band.minEur == null && band.maxEur == null)) return {};
    return {
      ...(band.minEur != null ? { minRevPerSiteEur: band.minEur } : {}),
      ...(band.maxEur != null ? { maxRevPerSiteEur: band.maxEur } : {}),
    };
  }, [sizeBands, sizeBand]);

  // Aggregation sizing intent → either aggTargetCount or aggAvgRevEur (EUR). Only
  // in aggregation mode with a set value; 'auto' derives from the pool median.
  const aggSizing = useMemo<{ aggTargetCount?: number; aggAvgRevEur?: number }>(() => {
    if (anchorMode !== 'aggregation' || aggSizeMode === 'auto') return {};
    const n = Number(aggSizeValue);
    if (!Number.isFinite(n) || n <= 0) return {};
    if (aggSizeMode === 'count') return { aggTargetCount: Math.round(n) };
    if (!revCcy) return {};
    return { aggAvgRevEur: Math.round(convertCurrency(n * 1e6, revCcy, 'EUR')) };
  }, [anchorMode, aggSizeMode, aggSizeValue, revCcy]);

  // Changing the core build inputs invalidates a previously-confirmed platform.
  // The attached portfolio is core too: its holdings are excluded from the pool,
  // so a confirmed anchor may no longer be eligible after a switch.
  const coreKey = `${country}|${subsegment}|${region}|${targetEur}|${objectives.join(',')}|${anchorMode}|${anchorMinRevEur ?? ''}|${sizeBand}|${excludeWeakQuality}|${aggSizing.aggTargetCount ?? ''}|${aggSizing.aggAvgRevEur ?? ''}|${attachedPortfolioId ?? ''}`;
  useEffect(() => {
    setPlatformEntityId(null);
    // A different build context — drop any replacement requests from the old one.
    setExcluded([]);
  }, [coreKey]);

  const query = useMemo<PlatformBuilderQuery | null>(() => {
    if (!country || !subsegment || targetEur <= 0 || objectives.length === 0) return null;
    return {
      country,
      subsegment,
      region: region || null,
      targetEur,
      objectives,
      anchorMode,
      ...(anchorMinRevEur != null ? { anchorMinRevEur } : {}),
      ...sizeFilter,
      ...(excludeWeakQuality ? { excludeWeakQuality: true } : {}),
      ...aggSizing,
      ...(platformEntityId ? { platformEntityId } : {}),
      ...(allExcludeIds.length > 0 ? { excludeEntityIds: allExcludeIds } : {}),
      ...(attachedMandateId ? { mandateId: attachedMandateId } : {}),
      ...(attachedPortfolioId ? { portfolioId: attachedPortfolioId } : {}),
    };
  }, [
    country,
    subsegment,
    region,
    targetEur,
    objectives,
    anchorMode,
    anchorMinRevEur,
    sizeFilter,
    excludeWeakQuality,
    aggSizing,
    platformEntityId,
    allExcludeIds,
    attachedMandateId,
    attachedPortfolioId,
  ]);

  const builder = usePlatformBuilder(query);
  const result = builder.data ?? null;

  const channelKey = useCatchmentKey(tabId, panelId);

  // Broadcast the site coordinates → a linked Map pins them (reuses the Roll-Up
  // pin layer). Entities without a geocoded site are dropped from the map only.
  const setRollupLink = useRollupLink((s) => s.setFor);
  useEffect(() => {
    const pts = (result?.portfolio ?? [])
      .filter((p) => p.latitude != null && p.longitude != null)
      .map((p) => ({
        lat: p.latitude as number,
        lng: p.longitude as number,
        name: p.name,
        synthetic: false,
        subsegment,
        entityId: p.entityId,
      }));
    if (pts.length === 0) {
      setRollupLink(channelKey, null);
      return;
    }
    setRollupLink(channelKey, {
      points: pts,
      subsegment: subsegment || null,
      country: country || null,
      label: `Platform build · ${subsegment}`,
    });
    return () => setRollupLink(channelKey, null);
  }, [result, channelKey, setRollupLink, subsegment, country]);

  // Open downstream panels — pinned Record for a candidate, or a Ward/Map linked
  // to THIS panel's channel (so they render the broadcast portfolio).
  const dockviewApiRef = useDockviewApi();
  const addPanel = useAppStore((s) => s.actions.addPanel);
  const pinPanel = useAppStore((s) => s.actions.pinPanel);
  const setPanelEntity = useAppStore((s) => s.actions.setPanelEntity);
  const thisPanel = usePanel(tabId, panelId);
  const gid: LinkGroupId = thisPanel
    ? thisPanel.link.kind === 'linked'
      ? thisPanel.link.groupId
      : thisPanel.link.lastGroupId
    : 'default';

  function openFloating(
    component: string,
    title: string,
    opts?: {
      groupId?: LinkGroupId;
      entityId?: string;
      initialState?: Record<string, unknown>;
      width?: number;
      height?: number;
    },
  ) {
    const api = dockviewApiRef.current;
    if (!api) return;
    const newId = addPanel(tabId, {
      component,
      title,
      ...(opts?.groupId ? { groupId: opts.groupId } : {}),
    });
    if (opts?.entityId) {
      pinPanel(tabId, newId);
      setPanelEntity(tabId, newId, opts.entityId);
    }
    const cascade = api.panels.filter((pp) => pp.api.location.type === 'floating').length;
    const off = 24 * cascade;
    api.addPanel({
      id: newId,
      component,
      title,
      params: {
        tabId,
        panelId: newId,
        ...(opts?.initialState ? { initialState: opts.initialState } : {}),
      },
      floating: {
        position: { left: 240 + off, top: 140 + off },
        width: opts?.width ?? 720,
        height: opts?.height ?? 600,
      },
    });
  }
  const openRecord = (entityId: string) => openFloating('company-overview', 'Record', { entityId });

  // Build-level export + actions (the buy-list as a CSV, a print-ready dossier,
  // and "Show all in Ward" which persists the portfolio as a STANDARD watchlist
  // and opens a normal Ward selected to it).
  const meta: BuildExportMeta = {
    country,
    subsegment,
    region: region || null,
    objectives,
    anchorMode,
    contextMode: result?.contextMode ?? 'form_context',
    mandateName: mandates.find((m) => m.id === attachedMandateId)?.name ?? null,
    portfolioName: attachedPortfolio?.name ?? null,
  };
  const createWatchlist = useCreateWatchlist();
  const addEntities = useAddEntitiesToWatchlist();
  const requestSelect = useSelectLink((s) => s.request);
  // Open (or focus) the Ward / Roll-Up on THIS channel — dockview-authoritative
  // dedup, so a linked panel is reused rather than duplicated. The select-link
  // request below then drives it to the list/foundation we just created.
  const openWard = useOpenChannelPanel(tabId, panelId, 'watchlist', 'Ward', {
    width: 860,
    height: 560,
  });
  const openRollUp = useOpenChannelPanel(tabId, panelId, 'rollup-simulator', 'Roll-Up', {
    width: 520,
    height: 620,
  });
  const [wardPending, setWardPending] = useState(false);
  async function showInWard() {
    if (!result || wardPending) return;
    setWardPending(true);
    try {
      const ids = result.portfolio.map((p) => p.entityId);
      const name = `${subsegment} · ${money(result.summary.targetEur, dispCcy)} build`;
      const res = await createWatchlist.mutateAsync(name);
      await addEntities.mutateAsync({ watchlistId: res.data.id, entityIds: ids });
      // Drive the channel's Ward to the new list, then open/focus it (a freshly
      // opened Ward reads the same request on mount).
      requestSelect(channelKey, 'watchlist', res.data.id);
      openWard();
    } finally {
      setWardPending(false);
    }
  }
  // Commit a Roll-Up Foundation, then DISPLAY it: select it on the channel's
  // Roll-Up (reused if linked, else opened).
  function onCommitted(foundationId: string) {
    requestSelect(channelKey, 'foundation', foundationId);
    openRollUp();
  }

  // Replace a suggested target — exclude it and let the build re-fill the gap
  // with the next-best alternative(s). If it was the confirmed anchor, release
  // the anchor too so a fresh one is nominated.
  function replaceTarget(entityId: string, name: string | null) {
    setExcluded((prev) =>
      prev.some((e) => e.id === entityId) ? prev : [...prev, { id: entityId, name }],
    );
    if (entityId === platformEntityId) setPlatformEntityId(null);
  }
  // Restore ONE replaced target — the build re-includes it next rebuild.
  const restoreTarget = (entityId: string) =>
    setExcluded((prev) => prev.filter((e) => e.id !== entityId));
  const resetReplacements = () => setExcluded([]);

  // Export CSV — fetch the targets as screener rows so the CSV carries EVERY
  // screener column (rendered exactly as the Screener/Watchlist show them).
  const [csvPending, setCsvPending] = useState(false);
  async function exportCsv() {
    if (!result || csvPending) return;
    setCsvPending(true);
    try {
      const ids = result.portfolio.map((p) => p.entityId);
      const { rows } = await fetchScreener({ entityIds: ids, limit: 10000 });
      exportPortfolioCsv(
        rows,
        ccy,
        (id: ScreenerRightColumnId) => t(SCREENER_COLUMN_DEFS[id].labelKey),
        `${subsegment}-${money(result.summary.targetEur, dispCcy)}`,
      );
    } finally {
      setCsvPending(false);
    }
  }

  const toggleObjective = (k: BuilderObjective) =>
    setObjectives((prev) => {
      if (prev.includes(k)) return prev.filter((o) => o !== k);
      // Concentrate and spread are mutually exclusive — adding one drops the other.
      const cleared = REGION_OBJECTIVES.includes(k)
        ? prev.filter((o) => !REGION_OBJECTIVES.includes(o))
        : prev;
      return [...cleared, k];
    });

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--s-3)',
          padding: 'var(--s-3)',
          height: '100%',
          overflow: 'auto',
        }}
      >
        {/* --- inputs --- */}
        <Section label="Buyer context">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}>
            {/* Mandate — attach one to prefill + steer the build (create/manage in
                the Mandate panel). */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--s-2)',
                alignItems: 'center',
              }}
            >
              <PanelSelect
                ariaLabel="Attach mandate"
                value={attachedMandateId ?? ''}
                onChange={(v) => setAttachedMandateId(v || null)}
                placeholder="mandate"
                width={FIELD_W}
                options={[
                  { value: '', label: 'No mandate' },
                  ...mandates.map((m) => ({ value: m.id, label: m.name })),
                ]}
              />
              <button
                type="button"
                onClick={() => openFloating('mandate', 'Mandate')}
                style={LINK_BTN}
              >
                {mandates.length ? 'Manage mandates ↗' : 'Create a mandate ↗'}
              </button>
              {attachedMandateId &&
                (() => {
                  const m = mandates.find((x) => x.id === attachedMandateId);
                  if (!m) return null;
                  const bits = [
                    m.subsegments.length ? m.subsegments.join(', ') : null,
                    m.clinicalSpecialisms.length
                      ? `${m.clinicalSpecialisms.length} specialism${m.clinicalSpecialisms.length > 1 ? 's' : ''}`
                      : null,
                    m.revMinEur || m.revMaxEur
                      ? `€${m.revMinEur ? m.revMinEur / 1_000_000 : '0'}–${m.revMaxEur ? m.revMaxEur / 1_000_000 : '∞'}m`
                      : null,
                  ].filter(Boolean);
                  return (
                    <span style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)' }}>
                      prefilled from mandate{bits.length ? ` · ${bits.join(' · ')}` : ''}
                    </span>
                  );
                })()}
            </div>
            {/* Portfolio — attach ZERO OR ONE named portfolio (§16). Only the
                selected portfolio's holdings are excluded from the buy-list and
                drive footprint gap/overlap; other portfolios stay dormant. */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--s-2)',
                alignItems: 'center',
              }}
            >
              <PanelSelect
                ariaLabel="Attach portfolio"
                value={attachedPortfolioId ?? ''}
                onChange={(v) => setAttachedPortfolioId(v || null)}
                placeholder="portfolio"
                width={FIELD_W}
                options={[
                  { value: '', label: 'No portfolio' },
                  ...portfolios.map((p) => ({
                    value: p.id,
                    label: `${p.name} (${p.holdingsCount})`,
                  })),
                ]}
              />
              <button
                type="button"
                onClick={() => openFloating('portfolio', 'Portfolio')}
                style={LINK_BTN}
              >
                {portfolios.length ? 'Manage portfolios ↗' : 'Create a portfolio ↗'}
              </button>
              <span style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)' }}>
                {attachedPortfolio
                  ? `${attachedPortfolio.holdingsCount} holding${attachedPortfolio.holdingsCount === 1 ? '' : 's'} excluded · footprint drives gap/overlap`
                  : 'No portfolio attached — conditions speak about the build, not a buyer.'}
              </span>
            </div>
          </div>
        </Section>

        <Section label="Build revenue target">
          <div
            style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', alignItems: 'center' }}
          >
            <div style={{ display: 'flex', gap: PAIR_GAP, alignItems: 'center' }}>
              <input
                value={targetInput}
                onChange={(e) => setTargetInput(e.target.value)}
                type="number"
                min="0"
                placeholder="insert amount"
                style={{ ...inputStyle, width: HALF_W, boxSizing: 'border-box' }}
                aria-label="Target revenue"
              />
              <PanelSelect
                ariaLabel="Currency"
                value={revCcy}
                onChange={(v) => setRevCcy(v as GlobalCurrency)}
                placeholder="currency"
                width={HALF_W}
                options={[
                  { value: '', label: 'currency', disabled: true },
                  ...GLOBAL_CURRENCIES.map((c) => ({ value: c, label: `${c}m` })),
                ]}
              />
            </div>
            <span style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)' }}>
              combined revenue to assemble
            </span>
          </div>
        </Section>

        <Section label="Market">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)' }}>
            <PanelSelect
              value={country}
              onChange={(v) => {
                setCountry(v);
                setRegion('');
              }}
              placeholder="country"
              width={FIELD_W}
              options={[
                { value: '', label: 'country', disabled: true },
                ...countryOptions.map((c) => ({
                  value: c,
                  label: MARKET_NAMES[c as MarketCode] ?? c,
                })),
              ]}
            />
            <PanelSelect
              value={subsegment}
              onChange={setSubsegment}
              placeholder="sub-segment"
              width={FIELD_W}
              options={[
                { value: '', label: 'sub-segment', disabled: true },
                ...CANONICAL_SUBSEGMENTS.map((s) => ({ value: s, label: s })),
              ]}
            />
            <PanelSelect
              value={region}
              onChange={setRegion}
              placeholder="all regions"
              width={FIELD_W}
              options={[
                { value: '', label: 'all regions' },
                ...regionOptions.map((r) => ({ value: r, label: r })),
              ]}
            />
          </div>
        </Section>

        <Section label="Operator size">
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', flexWrap: 'wrap' }}
          >
            <PanelSelect
              ariaLabel="Operator size"
              value={sizeBand}
              onChange={setSizeBand}
              width={FIELD_W}
              options={sizeBands.map((b) => ({ value: b.key, label: b.label }))}
            />
            <span style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)' }}>
              revenue per site — bigger operators mean fewer, larger deals
            </span>
          </div>
        </Section>

        <Section label="Objectives">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-1)' }}>
            {OBJECTIVES.map((o) => {
              const on = objectives.includes(o.key);
              return (
                <button
                  key={o.key}
                  type="button"
                  title={o.hint}
                  onClick={() => toggleObjective(o.key)}
                  style={{
                    ...chipStyle,
                    width: CHIP_W,
                    justifyContent: 'center',
                    ...(on ? chipOnStyle : {}),
                  }}
                >
                  {on && <Check size={11} />} {o.label}
                </button>
              );
            })}
          </div>
        </Section>

        <Section label="Structure">
          <div
            style={{ display: 'flex', gap: 'var(--s-1)', flexWrap: 'wrap', alignItems: 'center' }}
          >
            <ModeBtn
              active={anchorMode === 'aggregation'}
              onClick={() => setAnchorMode('aggregation')}
            >
              Aggregation
            </ModeBtn>
            <ModeBtn
              active={anchorMode === 'anchor-led'}
              onClick={() => setAnchorMode('anchor-led')}
            >
              Anchor-led
            </ModeBtn>
            {anchorMode === 'aggregation' ? (
              <>
                <span style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)' }}>
                  comparable operators —
                </span>
                <PanelSelect
                  ariaLabel="Cluster sizing"
                  value={aggSizeMode}
                  onChange={(v) => setAggSizeMode(v as 'auto' | 'count' | 'size')}
                  width={130}
                  options={[
                    { value: 'auto', label: 'auto size' },
                    { value: 'count', label: '# of targets' },
                    { value: 'size', label: `~${dispCcy}m each` },
                  ]}
                />
                {aggSizeMode !== 'auto' && (
                  <input
                    value={aggSizeValue}
                    onChange={(e) => setAggSizeValue(e.target.value)}
                    type="number"
                    min="0"
                    style={{ ...inputStyle, width: 64 }}
                    aria-label="Cluster size value"
                    placeholder={aggSizeMode === 'count' ? 'e.g. 10' : 'e.g. 2'}
                  />
                )}
              </>
            ) : (
              <>
                <span style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)' }}>
                  anchor at least
                </span>
                <input
                  value={anchorSize}
                  onChange={(e) => setAnchorSize(e.target.value)}
                  type="number"
                  min="0"
                  style={{ ...inputStyle, width: 60 }}
                  aria-label="Anchor size"
                />
                <PanelSelect
                  ariaLabel="Anchor size unit"
                  value={anchorUnit}
                  onChange={(v) => setAnchorUnit(v as 'pct' | 'abs')}
                  width={120}
                  options={[
                    { value: 'pct', label: '% of target' },
                    { value: 'abs', label: `${dispCcy}m` },
                  ]}
                />
              </>
            )}
          </div>
        </Section>

        <Section label="Constraints">
          <div
            style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap', alignItems: 'center' }}
          >
            <button
              type="button"
              onClick={() => setExcludeWeakQuality((v) => !v)}
              title="Exclude operators in the bottom regulatory-quality band (where the market carries a quality signal)"
              style={{ ...chipStyle, ...(excludeWeakQuality ? chipOnStyle : {}) }}
            >
              {excludeWeakQuality && <Check size={11} />} Exclude weak-rated
            </button>
          </div>
        </Section>

        {/* --- output --- */}
        {!query ? (
          <BuildEmptyState
            amount={Number(targetInput) > 0}
            currency={!!revCcy}
            country={!!country}
            subsegment={!!subsegment}
          />
        ) : builder.isLoading ? (
          <Empty>Constructing portfolio…</Empty>
        ) : builder.isError ? (
          <Empty>Couldn’t build a portfolio for these inputs.</Empty>
        ) : result ? (
          <Output
            result={result}
            ccy={dispCcy}
            openRecord={openRecord}
            onSelectPlatform={setPlatformEntityId}
            anchorMode={anchorMode}
            gid={gid}
            openFloating={openFloating}
            onShowInWard={showInWard}
            wardPending={wardPending}
            onCommitted={onCommitted}
            onExportCsv={exportCsv}
            csvPending={csvPending}
            onMemo={() => openPortfolioInvestmentMemo(result, meta, dispCcy)}
            onReplace={replaceTarget}
            replaced={excluded}
            onRestore={restoreTarget}
            onResetReplacements={resetReplacements}
            rebuilding={builder.isFetching}
          />
        ) : null}
      </div>
    </PanelFrame>
  );
}

// --- output (fill bar + single read-out table + actions) -------------------

function Output({
  result,
  ccy,
  openRecord,
  onSelectPlatform,
  anchorMode,
  gid,
  openFloating,
  onShowInWard,
  wardPending,
  onCommitted,
  onExportCsv,
  csvPending,
  onMemo,
  onReplace,
  replaced,
  onRestore,
  onResetReplacements,
  rebuilding,
}: {
  result: PlatformBuilderResult;
  ccy: GlobalCurrency;
  openRecord: (entityId: string) => void;
  onSelectPlatform: (id: string) => void;
  anchorMode: BuilderAnchorMode;
  gid: LinkGroupId;
  openFloating: (
    component: string,
    title: string,
    opts?: {
      groupId?: LinkGroupId;
      width?: number;
      height?: number;
      initialState?: Record<string, unknown>;
    },
  ) => void;
  onShowInWard: () => void;
  wardPending: boolean;
  onCommitted: (foundationId: string) => void;
  onExportCsv: () => void;
  csvPending: boolean;
  onMemo: () => void;
  onReplace: (entityId: string, name: string | null) => void;
  replaced: ReplacedTarget[];
  onRestore: (entityId: string) => void;
  onResetReplacements: () => void;
  rebuilding: boolean;
}) {
  const s = result.summary;
  // The bar fills to 100%; the read-out shows the TRUE percentage — a build can
  // overshoot the target because operators are indivisible (the closing bolt-on
  // tips it over), so "£11.1m of £10.0m" must read 111%, not a misleading 100%.
  const rawPct = Math.round((s.assembledEur / Math.max(1, s.targetEur)) * 100);
  const barPct = Math.min(100, rawPct);
  const overshoot = s.assembledEur > s.targetEur * 1.02;
  const confColor =
    s.confidence === 'High'
      ? 'var(--positive, #0a7d5a)'
      : s.confidence === 'Moderate'
        ? 'var(--color-warning-text, #854f0b)'
        : 'var(--negative, #c0143c)';
  const platformRev = result.portfolio.find((p) => p.role === 'platform')?.turnoverEur ?? null;
  const activePlatform = result.portfolio.find((p) => p.role === 'platform')?.entityId;
  const entityIds = result.portfolio.map((p) => p.entityId);

  return (
    <>
      {anchorMode === 'anchor-led' &&
        result.platformCandidates &&
        result.platformCandidates.length > 0 && (
          <Section label="Platform anchor">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-1)' }}>
              {result.platformCandidates.map((c) => (
                <button
                  key={c.entityId}
                  type="button"
                  onClick={() => {
                    onSelectPlatform(c.entityId);
                    openRecord(c.entityId);
                  }}
                  style={{ ...chipStyle, ...(activePlatform === c.entityId ? chipOnStyle : {}) }}
                  title="Anchor on this company and open its Record"
                >
                  {activePlatform === c.entityId && <Check size={11} />} {c.name ?? EMPTY_VALUE} ·{' '}
                  {money(c.turnoverEur, ccy)}
                </button>
              ))}
            </div>
          </Section>
        )}

      {/* fill-to-target — only ever short of 100% when inventory-constrained */}
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 'var(--t-meta)',
            color: 'var(--text-secondary)',
            marginBottom: 3,
          }}
        >
          <span>Fill to target</span>
          <span>
            {money(s.assembledEur, ccy)} of {money(s.targetEur, ccy)} · {rawPct}%
            {overshoot && (
              <span style={{ color: 'var(--text-tertiary)' }}>
                {' '}
                · overshoot (indivisible targets)
              </span>
            )}
          </span>
        </div>
        <div
          style={{ height: 6, background: 'var(--hairline)', borderRadius: 3, overflow: 'hidden' }}
        >
          <div
            style={{
              width: `${barPct}%`,
              height: '100%',
              background: s.feasible ? 'var(--accent)' : confColor,
            }}
          />
        </div>
        {/* Build-level conditions — the acquisition-language readout (spec §11:
            3-6 chips, positives first, risks claret; hover = evidence+caveats). */}
        {(result.conditions?.length ?? 0) > 0 && (
          <ConditionChips conditions={orderedConditions(result.conditions!)} max={6} />
        )}
      </div>

      {/* warnings sit directly under the bar, at table-text size */}
      {!s.feasible && (
        <Warn>
          Inventory-constrained — only {money(s.buildableRevEur, ccy)} is acquirable
          {anchorMode === 'anchor-led' && s.platformRevEur != null
            ? ` in operators no larger than the ${money(s.platformRevEur, ccy)} anchor (raise the anchor floor to unlock bigger targets)`
            : s.buildableRevEur < s.poolRevEur
              ? ` in comparable operators (each ≤ half your ${money(s.targetEur, ccy)} target, so the build is a real cluster) — the rest of the ${money(s.poolRevEur, ccy)} pool is in larger operators that suit an anchor-led build`
              : ' in this market'}{' '}
          vs your {money(s.targetEur, ccy)} target.
        </Warn>
      )}
      {s.anchorFallback && (
        <Warn>
          No independent large enough to anchor was available — built as an aggregation instead.
        </Warn>
      )}
      {s.oversized && (
        <Warn>
          {s.dealCount} targets is a screening universe, not a single-hold buy plan — a typical
          platform here is ~{s.typicalBuildLabel}. The median operator is just{' '}
          {money(s.poolMedianEur, ccy)}, so reaching {money(s.targetEur, ccy)} takes many small
          deals. To narrow it: set an Operator size band, switch to Anchor-led, or lower the target.
        </Warn>
      )}
      {s.regionUnverifiedCount > 0 && (
        <Warn>
          {s.regionUnverifiedCount} target{s.regionUnverifiedCount === 1 ? '' : 's'} placed by
          registered office in {s.coreRegion ?? 'this region'} — no regulated site we can geocode to
          confirm the catchment sits in-region. Operators with a site known to be elsewhere are
          already excluded.
        </Warn>
      )}
      {/* Constraint told the user it did nothing — never let a filter silently
          look applied. Market-agnostic wording (no regulator named). */}
      {s.qualityGateApplied && !s.qualitySignalAvailable && (
        <Warn>
          The weak-rated exclusion is inactive here — this market / sub-segment carries no
          regulatory-quality signal, so no operator could be filtered on quality.
        </Warn>
      )}

      {/* single read-out table — each fact stated once */}
      <div
        style={{
          border: '1px solid var(--hairline)',
          borderRadius: 6,
          padding: '2px 12px',
          background: 'var(--surface)',
        }}
      >
        <Row label="Targets">
          {s.dealCount}
          {s.platformCount > 0 ? ' + platform' : ''}
          <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--t-meta)' }}>
            {' · typical platform ~'}
            {s.typicalBuildLabel}
          </span>
        </Row>
        {s.platformCount > 0 && s.platformName && (
          <Row label="Platform">
            {s.platformName} · {money(platformRev, ccy)}
          </Row>
        )}
        <Row label="Core region">
          {s.coreRegion ?? '—'}
          {s.regions.length > 1 ? ` · spans ${s.regions.length} regions` : ''}
        </Row>
        <Row label="Eligible pool">
          {formatCount(s.poolSize)} operators · {money(s.poolRevEur, ccy)}
        </Row>
        <Row label="Market">
          {s.trueConsolidation} consolidation · {s.visibility} visibility in our data
        </Row>
        <Row label="Confidence" last>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span
              style={{
                alignSelf: 'flex-start',
                fontSize: 'var(--t-meta)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                color: '#fff',
                background: confColor,
                padding: '2px 9px',
                borderRadius: 3,
              }}
            >
              {s.confidence}
            </span>
            <span style={{ color: 'var(--text-secondary)' }}>{s.benchmarkNote}</span>
          </div>
        </Row>
      </div>

      {/* the buy-list itself — each suggested target, replaceable in place */}
      <TargetList
        result={result}
        ccy={ccy}
        openRecord={openRecord}
        onReplace={onReplace}
        replaced={replaced}
        onRestore={onRestore}
        onResetReplacements={onResetReplacements}
        rebuilding={rebuilding}
      />

      {/* Actions, in two tiers so the two button SIZES never sit shoulder-to-
          shoulder (md leads vs sm tools — each keeps its cross-panel size).
          Row 1 = the decisions (commit the build somewhere); row 2, under a
          hairline = the outputs/tools. Each row is internally one size. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)', alignItems: 'center' }}>
          <button
            type="button"
            onClick={onShowInWard}
            disabled={wardPending}
            style={btnStrongStyle}
          >
            <Users size={12} /> {wardPending ? 'Opening…' : 'Show all in Ward'}
          </button>
          <CommitBar
            entityIds={entityIds}
            name={`${s.coreRegion ? s.coreRegion + ' · ' : ''}${money(s.targetEur, ccy)} build`}
            disabled={entityIds.length === 0}
            onCommitted={onCommitted}
          />
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--s-2)',
            alignItems: 'center',
            paddingTop: 'var(--s-2)',
            borderTop: '1px solid var(--hairline)',
          }}
        >
          <button
            type="button"
            onClick={() =>
              openFloating('map', 'Map', {
                groupId: gid,
                width: 880,
                height: 640,
                // Seed the Map with THIS build's located targets so it shows the
                // shortlist, not the universe (the Map ignores passive channel
                // broadcasts; an explicit initialState seed is the way to scope it).
                initialState: {
                  portfolioPoints: result.portfolio
                    .filter((p) => p.latitude != null && p.longitude != null)
                    .map((p) => ({
                      lat: p.latitude,
                      lng: p.longitude,
                      name: p.name,
                      entityId: p.entityId,
                    })),
                },
              })
            }
            style={btnStyle}
          >
            <MapPin size={12} /> Show all on map
          </button>
          <button type="button" onClick={onExportCsv} disabled={csvPending} style={btnStyle}>
            <Download size={12} /> {csvPending ? 'Exporting…' : 'Export CSV'}
          </button>
          <button type="button" onClick={onMemo} style={btnStyle}>
            <FileText size={12} /> Investment Memo
          </button>
        </div>
      </div>
    </>
  );
}

// The suggested buy-list, each target replaceable in place. "Replace" excludes
// the entity and the build re-fills the gap with the next-best alternative(s)
// (auto-rebuild — the panel's query carries the exclusion set). The whole list
// dims while the rebuild is in flight so a click reads as "working".
// --- condition chips (build summary + buy-list rows) ------------------------

// Spec §11 chip ordering: role-derived first, then buyer/build fit, then the
// X-factor conditions. Within a band the server's emission order stands.
const CONDITION_ORDER: Record<string, number> = {
  platform_ready: 0,
  bolt_on_fit: 0,
  footprint_gap: 1,
  cannibalisation_risk: 1,
};

function orderedConditions(conditions: Condition[]): Condition[] {
  return [...conditions].sort(
    (a, b) => (CONDITION_ORDER[a.token] ?? 2) - (CONDITION_ORDER[b.token] ?? 2),
  );
}

/** Hover text: the evidence-bound note, then the evidence lines, then caveats —
 *  the memo shows the same content, so the chip never claims more than it. */
function conditionTooltip(c: Condition): string {
  const ev = c.evidence.map((e) => `• ${e.label}: ${e.value}`).join('\n');
  const cav = c.caveats.length ? `\nCaveats:\n${c.caveats.map((x) => `• ${x}`).join('\n')}` : '';
  return `${c.note}\n${ev}${cav}`;
}

function ConditionChips({ conditions, max }: { conditions: Condition[]; max: number }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3 }}>
      {conditions.slice(0, max).map((c) => {
        const risk = c.tone === 'risk';
        const color = risk ? 'var(--negative,#c0143c)' : 'var(--accent)';
        return (
          <span
            key={`${c.token}:${c.appliesTo}`}
            title={conditionTooltip(c)}
            style={{
              fontSize: '9px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '.03em',
              color,
              border: `1px solid color-mix(in srgb, ${color} ${risk ? 45 : 40}%, transparent)`,
              borderRadius: 8,
              padding: '0 6px',
              whiteSpace: 'nowrap',
            }}
          >
            {risk ? '⚠ ' : ''}
            {c.label}
          </span>
        );
      })}
    </div>
  );
}

function TargetList({
  result,
  ccy,
  openRecord,
  onReplace,
  replaced,
  onRestore,
  onResetReplacements,
  rebuilding,
}: {
  result: PlatformBuilderResult;
  ccy: GlobalCurrency;
  openRecord: (entityId: string) => void;
  onReplace: (entityId: string, name: string | null) => void;
  replaced: ReplacedTarget[];
  onRestore: (entityId: string) => void;
  onResetReplacements: () => void;
  rebuilding: boolean;
}) {
  const items = result.portfolio;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-1)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--s-2)',
        }}
      >
        <div
          style={{
            fontSize: 'var(--t-meta)',
            textTransform: 'uppercase',
            letterSpacing: '.06em',
            color: 'var(--text-tertiary)',
            fontWeight: 600,
          }}
        >
          Buy-list — {items.length} target{items.length === 1 ? '' : 's'}
          {rebuilding && (
            <span
              style={{
                marginLeft: 8,
                textTransform: 'none',
                letterSpacing: 0,
                fontWeight: 400,
                color: 'var(--text-tertiary)',
              }}
            >
              · rebuilding…
            </span>
          )}
        </div>
        {replaced.length > 0 && (
          <button
            type="button"
            onClick={onResetReplacements}
            title="Restore every replaced target and rebuild from scratch"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              border: 'none',
              background: 'transparent',
              color: 'var(--accent)',
              cursor: 'pointer',
              fontSize: 'var(--t-meta)',
              padding: 0,
            }}
          >
            <RotateCcw size={11} /> reset all ({replaced.length})
          </button>
        )}
      </div>
      <div
        style={{
          border: '1px solid var(--hairline)',
          borderRadius: 6,
          overflow: 'hidden',
          background: 'var(--surface)',
          opacity: rebuilding ? 0.55 : 1,
          transition: 'opacity .12s',
          pointerEvents: rebuilding ? 'none' : 'auto',
        }}
      >
        {/* column header — Conf/Revenue right-align to the fixed-width cells below */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--s-2)',
            padding: '4px 10px',
            borderBottom: '1px solid var(--hairline)',
            fontSize: '9px',
            textTransform: 'uppercase',
            letterSpacing: '.05em',
            color: 'var(--text-tertiary)',
          }}
        >
          <span style={{ flex: '1 1 0', minWidth: 0 }}>Target</span>
          <span
            style={{ flexShrink: 0, width: 56, textAlign: 'right' }}
            title="Revenue-estimate confidence band (P100 = filed)"
          >
            Rev conf
          </span>
          <span style={{ flexShrink: 0, width: 92, textAlign: 'right' }}>Revenue</span>
          <span style={{ flexShrink: 0, width: 18 }} />
        </div>
        {items.map((p, i) => {
          const conditions = orderedConditions(p.conditions ?? []);
          return (
            <div
              key={p.entityId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--s-2)',
                padding: '6px 10px',
                borderBottom: i < items.length - 1 ? '1px solid var(--hairline)' : 'none',
                fontSize: 'var(--t-body)',
              }}
            >
              <div
                style={{
                  flex: '1 1 0',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <button
                  type="button"
                  onClick={() => openRecord(p.entityId)}
                  title="Open Record"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    margin: 0,
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    font: 'inherit',
                    fontWeight: 600,
                    textAlign: 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  {p.name ?? EMPTY_VALUE}
                </button>
                {/* Why this pick — the ranking decomposition (also the "why" for a
                  target swapped in via Replace). */}
                {p.reason && (
                  <span
                    style={{
                      fontSize: 'var(--t-meta)',
                      color: 'var(--text-tertiary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {p.reason}
                  </span>
                )}
                {/* Conditions — WHY this pick, in acquisition language (server-
                  computed, evidence-bound). Up to 3 compact chips: role first,
                  then buyer/build fit, then X-factor; hover = note + evidence +
                  caveats. Risks render claret. */}
                {conditions.length > 0 && <ConditionChips conditions={conditions} max={3} />}
              </div>
              {p.role === 'platform' && (
                <span
                  style={{
                    flexShrink: 0,
                    fontSize: '9px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '.05em',
                    color: 'var(--accent)',
                    border: '1px solid var(--accent)',
                    borderRadius: 2,
                    padding: '0 4px',
                  }}
                >
                  Anchor
                </span>
              )}
              <span
                style={{
                  flexShrink: 0,
                  color: 'var(--text-tertiary)',
                  fontSize: 'var(--t-meta)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 130,
                }}
              >
                {p.region ?? '—'}
              </span>
              {/* Revenue-estimate confidence — P100 = filed/reported (toned to signal
                it's not modelled); lower P = modelled estimate (±50% band). */}
              <span
                title={confidenceTitle(p.turnoverConfidenceP, p.turnoverBasis)}
                style={{
                  flexShrink: 0,
                  width: 56,
                  textAlign: 'right',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--t-meta)',
                  whiteSpace: 'nowrap',
                  color:
                    p.turnoverConfidenceP === 'P100' ? 'var(--positive)' : 'var(--text-tertiary)',
                }}
              >
                {p.turnoverConfidenceP ?? '—'}
              </span>
              <span
                style={{
                  flexShrink: 0,
                  width: 92,
                  textAlign: 'right',
                  fontFamily: 'var(--font-mono)',
                  whiteSpace: 'nowrap',
                }}
              >
                {money(p.turnoverEur, ccy)}
              </span>
              <button
                type="button"
                onClick={() => onReplace(p.entityId, p.name)}
                title="Replace — swap this target for the next-best alternative"
                aria-label={`Replace ${p.name ?? 'target'}`}
                onMouseOver={(e) => (e.currentTarget.style.color = 'var(--accent)')}
                onMouseOut={(e) => (e.currentTarget.style.color = 'var(--text-tertiary)')}
                style={{
                  flexShrink: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-tertiary)',
                  cursor: 'pointer',
                  padding: 3,
                }}
              >
                <RefreshCw size={12} />
              </button>
            </div>
          );
        })}
      </div>
      {/* Replaced targets — each restorable on its own (a chip per entity), so
          undoing one replacement never wipes the rest. "reset all" lives in the
          header above for the full restore. */}
      {replaced.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              fontSize: 'var(--t-meta)',
              textTransform: 'uppercase',
              letterSpacing: '.06em',
              color: 'var(--text-tertiary)',
              fontWeight: 600,
            }}
          >
            Replaced — restore:
          </span>
          {replaced.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => onRestore(r.id)}
              title={`Restore ${r.name ?? 'this target'} to the buy-list`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                maxWidth: 220,
                fontSize: 'var(--t-meta)',
                border: '1px solid var(--hairline)',
                background: 'var(--menu-surface)',
                color: 'var(--text-secondary)',
                borderRadius: 2,
                padding: '1px 6px',
                cursor: 'pointer',
              }}
            >
              <RotateCcw size={10} style={{ flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.name ?? EMPTY_VALUE}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Warn({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 'var(--t-body)',
        color: 'var(--color-warning-text, #854f0b)',
        background: 'var(--color-warning-bg, #faeeda)',
        border: '1px solid var(--hairline)',
        borderRadius: 4,
        padding: '6px 10px',
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

// --- commit -----------------------------------------------------------------

function CommitBar({
  entityIds,
  name,
  disabled,
  onCommitted,
}: {
  entityIds: string[];
  name: string;
  disabled: boolean;
  onCommitted: (foundationId: string) => void;
}) {
  const createFoundation = useCreateRollupFoundation();
  const addEntities = useAddEntitiesToWatchlist();
  const [done, setDone] = useState(false);
  const pending = createFoundation.isPending || addEntities.isPending;

  async function commit() {
    setDone(false);
    const res = await createFoundation.mutateAsync(name);
    await addEntities.mutateAsync({ watchlistId: res.data.id, entityIds });
    setDone(true);
    // Open / focus the Roll-Up on this channel, selected to the new foundation.
    onCommitted(res.data.id);
  }

  return (
    <>
      <button type="button" onClick={commit} disabled={disabled || pending} style={btnStrongStyle}>
        {pending ? 'Committing…' : 'Commit to Roll-Up Foundation'}
      </button>
      {done && (
        <span style={{ fontSize: 'var(--t-meta)', color: 'var(--positive, #0a7d5a)' }}>
          <Check size={11} style={{ verticalAlign: '-1px' }} /> Opened in Roll-Up — model & curate.
        </span>
      )}
    </>
  );
}

// --- custom dropdown (matches the Watchlist picker) -------------------------

// A native <select>'s popup renders too small inside the floating panel (the OS
// menu doesn't track the panel transform). The Watchlist picker avoids this by
// being a custom DOM menu — so we mirror it: a button trigger + a portaled menu
// at var(--t-body), items styled exactly like WatchlistPicker. Portaled (fixed)
// so the panel's overflow:auto can't clip it.
interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}
function PanelSelect({
  value,
  onChange,
  options,
  placeholder,
  width,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  width?: number | string;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    place();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value && !(o.value === '' && o.disabled));
  const showLabel = selected ? selected.label : (placeholder ?? '');
  const menuOptions = options.filter((o) => !(o.value === '' && o.disabled));

  return (
    <div style={{ display: 'inline-block', width }}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        style={{
          ...selectStyle,
          width: '100%',
          boxSizing: 'border-box',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 6,
          cursor: 'pointer',
          textAlign: 'left',
          color: value ? 'var(--text-primary)' : 'var(--text-tertiary)',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {showLabel}
        </span>
        <ChevronDown size={12} style={{ flexShrink: 0, opacity: 0.6 }} />
      </button>
      {open &&
        rect &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: 'fixed',
              top: rect.top,
              left: rect.left,
              minWidth: rect.width,
              maxHeight: 300,
              overflowY: 'auto',
              background: 'var(--menu-surface)',
              border: '1px solid var(--hairline)',
              borderRadius: 2,
              boxShadow: '0 6px 24px rgba(10,14,26,0.14), 0 2px 6px rgba(10,14,26,0.08)',
              zIndex: 9000,
              padding: 4,
              fontSize: 'var(--t-body)',
            }}
          >
            {menuOptions.map((o) => {
              const isSel = o.value === value;
              return (
                <div
                  key={o.value}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  onMouseOver={(e) => {
                    if (!isSel) e.currentTarget.style.background = 'var(--bg-sunken)';
                  }}
                  onMouseOut={(e) => {
                    if (!isSel) e.currentTarget.style.background = 'transparent';
                  }}
                  style={{
                    padding: '4px 6px',
                    cursor: 'pointer',
                    borderRadius: 2,
                    whiteSpace: 'nowrap',
                    color: 'var(--text-primary)',
                    background: isSel ? 'var(--color-accent-100)' : 'transparent',
                  }}
                >
                  {o.label}
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}

// --- bits -------------------------------------------------------------------

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-1)' }}>
      <div
        style={{
          fontSize: 'var(--t-meta)',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          color: 'var(--text-tertiary)',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Row({ label, children, last }: { label: string; children: ReactNode; last?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--s-2)',
        padding: '7px 0',
        borderBottom: last ? 'none' : '1px solid var(--hairline)',
        fontSize: 'var(--t-body)',
        lineHeight: 1.5,
      }}
    >
      <div
        style={{
          width: 116,
          flexShrink: 0,
          fontSize: 'var(--t-meta)',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
          color: 'var(--text-tertiary)',
          fontWeight: 600,
          paddingTop: 2,
        }}
      >
        {label}
      </div>
      <div style={{ flex: 1, minWidth: 0, color: 'var(--text-primary)' }}>{children}</div>
    </div>
  );
}

function ModeBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...chipStyle,
        width: CHIP_W,
        justifyContent: 'center',
        ...(active ? chipOnStyle : {}),
      }}
    >
      {children}
    </button>
  );
}

// Guided empty state — an accent icon, a one-line value prop, and a checklist
// that ticks off the required inputs as the user fills them (live progress).
function BuildEmptyState({
  amount,
  currency,
  country,
  subsegment,
}: {
  amount: boolean;
  currency: boolean;
  country: boolean;
  subsegment: boolean;
}) {
  const items = [
    { label: 'Target amount', done: amount },
    { label: 'Currency', done: currency },
    { label: 'Country', done: country },
    { label: 'Sub-segment', done: subsegment },
  ];
  const done = items.filter((i) => i.done).length;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-3)',
        padding: 'var(--s-2) 0',
        maxWidth: 440,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 8,
            display: 'grid',
            placeItems: 'center',
            background: 'var(--color-accent-100)',
            color: 'var(--accent)',
            flexShrink: 0,
          }}
        >
          <Layers size={18} />
        </div>
        <div>
          <div
            style={{
              fontSize: '15px',
              fontWeight: 700,
              color: 'var(--text-primary)',
              lineHeight: 1.1,
            }}
          >
            Construct a platform
          </div>
          <div style={{ fontSize: 'var(--t-meta)', color: 'var(--text-tertiary)', marginTop: 2 }}>
            {done} of {items.length} set
          </div>
        </div>
      </div>
      <div style={{ fontSize: 'var(--t-body)', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
        Set the inputs and Corryk assembles a target buy-list — sized to your revenue goal, ranked
        by the signals, and geographically clustered.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {items.map((i) => (
          <div
            key={i.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--s-2)',
              fontSize: 'var(--t-body)',
            }}
          >
            <span
              style={{
                width: 14,
                height: 14,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                boxSizing: 'border-box',
                background: i.done ? 'var(--accent)' : 'var(--surface)',
                border: `1px solid ${i.done ? 'var(--accent)' : 'var(--hairline)'}`,
                color: 'var(--bg)',
              }}
            >
              {i.done && <Check size={10} strokeWidth={3} />}
            </span>
            <span style={{ color: i.done ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
              {i.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 'var(--t-body)',
        color: 'var(--text-secondary)',
        padding: 'var(--s-3) 0',
        lineHeight: 1.6,
      }}
    >
      {children}
    </div>
  );
}

const selectStyle: CSSProperties = {
  fontSize: 'var(--t-body)',
  padding: '2px var(--s-2)',
  border: '1px solid var(--hairline)',
  borderRadius: 2,
  background: 'var(--menu-surface)',
  color: 'var(--text-primary)',
};
const inputStyle: CSSProperties = {
  fontSize: 'var(--t-body)',
  padding: '2px var(--s-2)',
  border: '1px solid var(--hairline)',
  borderRadius: 2,
  background: 'var(--menu-surface)',
  color: 'var(--text-primary)',
};
const chipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  boxSizing: 'border-box',
  fontSize: 'var(--t-body)',
  padding: '3px 10px',
  // Non-shorthand border props so the active state can override borderColor
  // without React's shorthand/non-shorthand conflict warning.
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'var(--hairline)',
  // Squared corners (radius 3) to match the platform's other controls.
  borderRadius: 3,
  background: 'var(--surface)',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
};
const chipOnStyle: CSSProperties = {
  background: 'var(--accent)',
  color: '#fff',
  borderColor: 'var(--accent)',
};
// Shared button system (see workspace/button.tsx): the standard control is
// `secondary`; the lead action (Show in Ward / Commit) is `primary`.
const btnStyle: CSSProperties = buttonStyle('secondary');
const btnStrongStyle: CSSProperties = buttonStyle('primary', 'md');
